import { differenceInCalendarDays, format, isValid, parseISO } from 'date-fns'
import frLocal from 'date-fns/locale/fr'
import { StatsConversationSummary } from '../../../model/StatsAssistant'

/**
 * Aperçu en texte brut d'une réponse markdown. L'aperçu est tronqué côté back : les marqueurs
 * peuvent être orphelins (`**74 vasect…`), on les retire donc sans chercher à les apparier.
 */
export const plainTextPreview = (markdown: string): string =>
  markdown
    .replace(/```[\s\S]*?(```|$)/g, ' ')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^\s{0,3}(#{1,6}|>+)\s*/gm, '')
    .replace(/^\s*([-*+]|\d+[.)])\s+/gm, '')
    .replace(/(\*\*|__|~~|`)/g, '')
    .replace(/(^|\s)[*_](?=\S)|(?<=\S)[*_](?=\s|$)/g, '$1')
    .replace(/\|/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

export type ConversationDateGroup = 'today' | 'yesterday' | 'week' | 'month' | 'older'

export const CONVERSATION_DATE_GROUP_LABELS: Record<ConversationDateGroup, string> = {
  today: "Aujourd'hui",
  yesterday: 'Hier',
  week: '7 derniers jours',
  month: '30 derniers jours',
  older: 'Plus ancien',
}

const dateGroupOf = (date: Date, now: Date): ConversationDateGroup => {
  const days = differenceInCalendarDays(now, date)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 7) return 'week'
  if (days < 30) return 'month'
  return 'older'
}

export interface ConversationGroup {
  key: ConversationDateGroup
  label: string
  items: StatsConversationSummary[]
}

/** Groupes dans l'ordre de la liste (triée par updatedAt décroissant côté back). */
export const groupConversationsByDate = (
  items: StatsConversationSummary[],
  now: Date = new Date(),
): ConversationGroup[] =>
  items.reduce<ConversationGroup[]>((groups, item) => {
    const date = parseISO(item.updatedAt)
    const key = isValid(date) ? dateGroupOf(date, now) : 'older'
    const last = groups[groups.length - 1]
    if (last?.key === key) {
      last.items.push(item)
    } else {
      groups.push({ key, label: CONVERSATION_DATE_GROUP_LABELS[key], items: [item] })
    }
    return groups
  }, [])

/** Heure pour aujourd'hui, jour + mois cette année, date courte au-delà. */
export const formatConversationDate = (updatedAt: string, now: Date = new Date()): string => {
  const date = parseISO(updatedAt)
  if (!isValid(date)) return ''
  if (differenceInCalendarDays(now, date) <= 0) return format(date, 'HH:mm')
  if (date.getFullYear() === now.getFullYear()) return format(date, 'd MMM', { locale: frLocal })
  return format(date, 'dd/MM/yy')
}
